import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { AuthLayout } from "@/components/layouts";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/formControls/Checkbox";
import { cn } from "@/lib/cn";

const scopes = [
  {
    id: "mcp:read",
    title: "Read skills data",
    description: "View and access your company skills information",
  },
  {
    id: "mcp:read_pii",
    title: "Read people data",
    description: "View and access personal data of people",
  },
  {
    id: "auth:mcp:read",
    title: "Read user & role data",
    description: "View users, roles, and permissions in your company",
  },
  {
    id: "auth:mcp:read_pii",
    title: "Read user personal data",
    description: "View names, email addresses and employee IDs of users unmasked",
  },
  {
    id: "auth:mcp:write",
    title: "Manage user roles",
    description: "Assign and remove role definitions for users",
  },
  {
    id: "skiller:maintenance:read",
    title: "Read SkillCheck invitations",
    description: "View candidate invitation state for maintenance and recovery",
  },
  {
    id: "skiller:maintenance:write",
    title: "Repair SkillCheck invitations",
    description: "Reopen invitations, re-query exam results and re-run assessments",
  },
];

interface ClientInfo {
  client_name?: string;
  scopes?: string[];
}

const fetchClientInfo = async (clientId: string, resource: string): Promise<ClientInfo> => {
  const resourceQuery = resource ? `&resource=${encodeURIComponent(resource)}` : "";
  const res = await fetch(
    `/api/oauth2/client-info?client_id=${encodeURIComponent(clientId)}${resourceQuery}`,
  );
  if (!res.ok) {
    throw new Error("Failed to fetch client info");
  }
  return res.json();
};

export const ConsentView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Extract OAuth parameters from URL
  const clientId = searchParams.get("client_id") || "";
  const redirectUri = searchParams.get("redirect_uri") || "";
  const responseType = searchParams.get("response_type") || "";
  const scope = searchParams.get("scope") || "";
  const state = searchParams.get("state") || "";
  const codeChallenge = searchParams.get("code_challenge") || "";
  const codeChallengeMethod = searchParams.get("code_challenge_method") || "";
  const resource = searchParams.get("resource") || "";

  // Parse requested scopes from URL
  const requestedScopes = scope.split(" ").filter(Boolean);

  const clientInfoQuery = useQuery({
    queryKey: ["oauthClientInfo", clientId, resource],
    queryFn: () => fetchClientInfo(clientId, resource),
    enabled: !!clientId,
    // A failure shows its own message; retrying would only keep the form waiting
    retry: false,
  });
  const clientInfo = clientInfoQuery.data;
  const clientName = clientInfo?.client_name || "Application";
  const isLoadingClientName = clientInfoQuery.isLoading;
  const clientInfoFailed = clientInfoQuery.isError;

  // Resolved scopes: from URL if present, otherwise filled from client-info API
  const allowedScopes = clientInfo?.scopes ?? [];
  const resolvedScopes =
    clientInfo && (resource || allowedScopes.length > 0)
      ? // If scopes were requested in URL, intersect with allowed scopes;
        // otherwise fall back to all allowed scopes from client registration
        scope
        ? requestedScopes.filter((s) => allowedScopes.includes(s))
        : allowedScopes
      : requestedScopes;

  const [selectedScopes, setSelectedScopes] = useState<Record<string, boolean>>({});

  // At least one scope must be selected (changed from all scopes)
  const atLeastOneScopeSelected = resolvedScopes.some((s) => selectedScopes[s]);

  // Get the list of selected scopes as a space-separated string
  const getSelectedScopesString = () => {
    return resolvedScopes.filter((s) => selectedScopes[s]).join(" ");
  };

  const toggleScope = (scopeId: string, checked: boolean) => {
    setSelectedScopes((prev) => ({ ...prev, [scopeId]: checked }));
  };

  const handleCancel = () => {
    // Redirect back to client with error
    if (redirectUri) {
      const errorUrl = new URL(redirectUri);
      errorUrl.searchParams.set("error", "access_denied");
      errorUrl.searchParams.set("error_description", "User denied authorization");
      if (state) {
        errorUrl.searchParams.set("state", state);
      }
      window.location.href = errorUrl.toString();
    } else {
      navigate("/login");
    }
  };

  // Filter scopes to only show those that were requested (or resolved from client)
  const displayScopes = scopes.filter((s) => resolvedScopes.includes(s.id));

  const resourceUnavailable =
    !!resource && !isLoadingClientName && (clientInfoFailed || resolvedScopes.length === 0);

  return (
    <AuthLayout title="Allow access to your account">
      <form
        method="POST"
        action="/oauth2/authorize"
        className="w-full max-w-md space-y-6 p-8 rounded-2xl bg-background/95 backdrop-blur-md shadow-elegant"
      >
        {/* Hidden fields to preserve OAuth parameters */}
        <input type="hidden" name="client_id" value={clientId} />
        <input type="hidden" name="redirect_uri" value={redirectUri} />
        <input type="hidden" name="response_type" value={responseType} />
        {/* Send only the selected scopes, not all requested scopes */}
        <input type="hidden" name="scope" value={getSelectedScopesString()} />
        {state && <input type="hidden" name="state" value={state} />}
        {codeChallenge && <input type="hidden" name="code_challenge" value={codeChallenge} />}
        {resource && <input type="hidden" name="resource" value={resource} />}
        {codeChallengeMethod && (
          <input type="hidden" name="code_challenge_method" value={codeChallengeMethod} />
        )}

        <div className="space-y-2 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-foreground">
            {isLoadingClientName
              ? "Application wants to access your SkillPanel account"
              : `${clientName} wants to access your SkillPanel account`}
          </h2>
          {resource && <p className="text-sm text-muted-foreground">Access to: {resource}</p>}
          {!resourceUnavailable && (
            <p className="text-sm text-muted-foreground">
              Select at least one permission you want to grant:
            </p>
          )}
        </div>

        <div className="space-y-3 py-4">
          {resourceUnavailable ? (
            <p className="text-sm text-muted-foreground text-center">
              {clientInfoFailed
                ? "Could not load the application details. Try again later."
                : "This application has no access to this server."}
            </p>
          ) : displayScopes.length > 0 ? (
            displayScopes.map((scopeItem) => (
              <div
                key={scopeItem.id}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-lg border transition-colors",
                  "bg-surface-subtle border-border hover:bg-muted",
                )}
              >
                <Checkbox
                  id={scopeItem.id}
                  name={`scope_${scopeItem.id}`}
                  checked={selectedScopes[scopeItem.id] || false}
                  onChange={(checked) => toggleScope(scopeItem.id, checked)}
                  label={scopeItem.title}
                  labelVisible={false}
                  size={20}
                />
                <div className="flex-1 space-y-1">
                  <label
                    htmlFor={scopeItem.id}
                    className="text-sm font-medium leading-none cursor-pointer text-foreground"
                  >
                    {scopeItem.title}
                  </label>
                  <p className="text-xs text-muted-foreground">{scopeItem.description}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center">No scopes requested</p>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            size="xl"
            className="flex-1"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          {!resourceUnavailable && (
            <Button
              type="submit"
              variant="brand"
              size="xl"
              className="flex-1"
              disabled={!atLeastOneScopeSelected}
            >
              Allow
            </Button>
          )}
        </div>

        <p className="text-xs text-center text-muted-foreground">
          Make sure you trust {clientName}. You may be sharing sensitive info with this application.
        </p>
      </form>
    </AuthLayout>
  );
};
